import useGlobals from 'sb-hooks/useGlobals'
import useRedirect from 'sb-hooks/useRedirect'
import { useIntl } from 'sb-react-intl'
import { useConnect } from 'sb-redux-hook'
import { useMemo, useWillMount } from 'sb-hooks'
import { formatPrice, links, string } from 'common/helpers'

import messages from './messages'


const getItems = ({ values, products }) => {
  if (!Array.isArray(values.productIds)) {
    return products
  }

  return products.filter(({ id }) => values.productIds.includes(String(id)))
}


const useCommonSeoPage = () => {
  const {
    meta, title, description, gender, values, products,
    isLoggedIn, subscriptionPrice, originalPathname,
  } = useConnect({
    meta: 'view.seoPage.meta',
    title: 'view.seoPage.title',
    description: 'view.seoPage.description',
    gender: 'view.seoPage.gender',
    values: 'view.seoPage.values',
    products: 'view.seoPage.products',
    isLoggedIn: 'models.me.isLoggedIn',
    subscriptionPrice: 'models.subscription.price',
    originalPathname: 'routing.location.pathname',
  })

  const items = getItems({ values, products })
  const isBot = useGlobals(({ globals }) => globals.device.isBot)

  const intl = useIntl()
  const redirect = useRedirect()

  useWillMount(() => {
    const isPerfumesMatched = /^\/(perfumes|colognes)/.test(originalPathname)

    if (isLoggedIn && isPerfumesMatched) {
      redirect(gender === 'male' ? links.subscription.colognes : links.subscription.perfumes)
    }
  })

  const { pathname, pageIndex } = useMemo(() => {
    let pathname = originalPathname
    let pageIndex = 0

    const iaNAPage = /na-(perfumes|colognes)/.test(pathname)

    if (iaNAPage && /\/\d+$/.test(pathname)) {
      pathname = pathname.replace(/\/\d+$/, '')
      pageIndex = pathname.replace(/^.+\//, '')
    }

    return {
      pathname,
      pageIndex,
    }
  }, [ originalPathname ])

  // ductape for 'kate-spade' brand
  const isDescriptionVisible = !pathname.includes('kate-spade')

  const isBurberryColognes = pathname === '/burberry-colognes'
  const isCalvinKleinPerfumes = pathname === '/calvin-klein-perfumes'
  const isSalvatoreFerragamoPerfumesForWomen = pathname === '/salvatore-ferragamo-perfumes-for-women'

  const price = useMemo(() => formatPrice(subscriptionPrice, true), [ subscriptionPrice ])

  const helmetTitle = useMemo(() => (
    isBurberryColognes
      ? intl.formatMessage({ ...messages.burberry.metaTitle, values: { price } })
      : `${meta.title} | Scentbird.com`
  ), [ intl, meta, price, isBurberryColognes ])

  const helmetMeta = useMemo(() => {
    const tags = [
      { name: 'description', content: meta.description ? string.multiLineToOneLine(meta.description) : '' },
      { name: 'keywords', content: meta.keywords },
    ]

    if (isBurberryColognes) {
      tags.push({
        name: 'title',
        content: intl.formatMessage({ ...messages.burberry.metaTitle, values: { price } }),
      })
    }

    return tags
  }, [ intl, meta, price, isBurberryColognes ])

  const brandName = values.name

  const pageTitle = useMemo(() => {
    if (isBurberryColognes) {
      return messages.burberry.title
    }

    return title
  }, [ title, isBurberryColognes ])

  const reviewsTitle = useMemo(() => {
    if (isBurberryColognes) {
      return { ...messages.reviewsTitle.burberry, values: { brandName } }
    }

    if (isSalvatoreFerragamoPerfumesForWomen || isCalvinKleinPerfumes) {
      return { ...messages.reviewsTitle.perfume, values: { brandName } }
    }

    return { ...messages.reviewsTitle.default, values: { brandName } }
  }, [ brandName, isBurberryColognes, isCalvinKleinPerfumes, isSalvatoreFerragamoPerfumesForWomen ])

  const pageTopDescription = useMemo(() => {
    if (isBurberryColognes) {
      return { ...messages.pageTopDescription, values: { price } }
    }

    return null
  }, [ isBurberryColognes, price ])

  const pageBottomDescription = useMemo(() => {
    if (isBurberryColognes) {
      return messages.pageBottomDescription
    }

    if (isDescriptionVisible) {
      return description
    }

    return null
  }, [ description, isBurberryColognes, isDescriptionVisible ])

  return {
    items,
    pathname,
    pageIndex,
    helmetTitle,
    helmetMeta,
    pageTitle,
    pageTopDescription,
    pageBottomDescription,
    reviewsTitle,
    price,
    productsAtPage: isBot ? 1000 : 50,
    isBurberryColognes,
  }
}


export default useCommonSeoPage
